import { useEffect, useState } from 'react';
import { tmdb, posterUrl, backdropUrl, yearFromReleaseDate } from '../lib/tmdb.js';
import services from '../config/services.json';
import { isLoved, isEjected, toggleLove, toggleEject, subscribeLikes } from '../lib/likes.js';
import { useAuth } from '../lib/auth.js';

// `movieId` is kept as the prop name for backwards compat but it now accepts
// either { tmdb_id, media_type } or a bare number (movie).
export default function MovieModal({ movieId, onClose }) {
  const { tmdbId, mediaType } = normalizeRef(movieId);
  const [details, setDetails] = useState(null);
  const [error, setError]     = useState(null);

  useEffect(() => {
    let alive = true;
    setDetails(null);
    setError(null);
    tmdb.media(tmdbId, mediaType)
      .then((d) => alive && setDetails(d))
      .catch((e) => alive && setError(e.message));
    return () => { alive = false; };
  }, [tmdbId, mediaType]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="hb-modal" role="dialog" aria-modal="true" onClick={onClose}>
      <div className="hb-modal__panel" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="hb-modal__close" onClick={onClose} aria-label="Close">×</button>
        {error && <div className="hb-modal__error">Couldn’t load this title: {error}</div>}
        {!details && !error && <div className="hb-modal__loading">Loading…</div>}
        {details && <Details details={details} mediaType={mediaType} />}
      </div>
    </div>
  );
}

function normalizeRef(ref) {
  if (typeof ref === 'object' && ref !== null) {
    return { tmdbId: ref.tmdb_id || ref.id, mediaType: ref.media_type || 'movie' };
  }
  return { tmdbId: ref, mediaType: 'movie' };
}

function Details({ details, mediaType }) {
  const year = yearFromReleaseDate(details.release_date);
  const meta = [];
  if (mediaType === 'tv') {
    if (details.number_of_seasons) {
      meta.push(`${details.number_of_seasons} season${details.number_of_seasons > 1 ? 's' : ''}`);
    }
    if (details.number_of_episodes) {
      meta.push(`${details.number_of_episodes} episodes`);
    }
  } else {
    meta.push(details.runtime ? `${details.runtime} min` : '—');
  }
  if (details.vote_average) meta.push(`★ ${details.vote_average.toFixed(1)}`);
  if (details.genres?.length) meta.push(details.genres.map((g) => g.name).join(' · '));

  const bg = backdropUrl(details.backdrop_path);

  return (
    <>
      {bg && <div className="hb-modal__backdrop" style={{ backgroundImage: `url(${bg})` }} />}
      <div className="hb-modal__body">
        <img className="hb-modal__poster" src={posterUrl(details.poster_path, 'w500')} alt="" />
        <div className="hb-modal__info">
          <h2 className="hb-modal__title">
            {details.title || details.name}
            {year ? <span className="hb-modal__year">({year})</span> : null}
            {mediaType === 'tv' && <span className="hb-modal__type-pill">TV</span>}
          </h2>
          <div className="hb-modal__meta">
            {meta.map((m, i) => <span key={i}>{m}</span>)}
          </div>
          {details.tagline ? <p className="hb-modal__tagline">“{details.tagline}”</p> : null}
          <p className="hb-modal__overview">{details.overview || 'No synopsis available.'}</p>
          <LoveButton tmdbId={details.id} mediaType={mediaType} />
          <WatchLinks watch={details.watch} title={details.title || details.name} />
        </div>
      </div>
    </>
  );
}

function LoveButton({ tmdbId, mediaType }) {
  const { user, profile } = useAuth();
  const [, force] = useState(0);
  const [err, setErr] = useState('');

  useEffect(() => subscribeLikes(() => force((n) => n + 1)), []);

  if (!user) {
    return (
      <div className="hb-love hb-love--locked">
        <em>Sign in to save loves to your profile.</em>
      </div>
    );
  }

  const loved   = isLoved(tmdbId, mediaType);
  const ejected = isEjected(tmdbId, mediaType);
  const displayName = profile?.display_name || 'You';

  async function clickLove() {
    setErr('');
    try { await toggleLove(tmdbId, mediaType); }
    catch (e) { setErr(e.message || 'Failed.'); }
  }
  async function clickEject() {
    setErr('');
    try { await toggleEject(tmdbId, mediaType); }
    catch (e) { setErr(e.message || 'Failed.'); }
  }

  return (
    <div className="hb-love">
      <div className="hb-love__label">
        {loved ? 'Loved.' : ejected ? 'Ejected — won’t recommend again.' : 'What do you think?'}
      </div>
      <div className="hb-love__row">
        <button
          type="button"
          className={'hb-love__btn' + (loved ? ' is-active' : '')}
          onClick={clickLove}
          aria-pressed={loved}
        >
          <span aria-hidden="true">{loved ? '❤' : '♡'}</span>
          {' '}{loved ? `${displayName} loves this` : `${displayName} love this`}
        </button>
        <button
          type="button"
          className={'hb-eject__btn' + (ejected ? ' is-active' : '')}
          onClick={clickEject}
          aria-pressed={ejected}
          title="Eject — won't recommend this or things like it"
        >
          <span aria-hidden="true">⏏</span> {ejected ? 'Ejected' : 'Eject'}
        </button>
      </div>
      {err && <div className="hb-signin__err">{err}</div>}
    </div>
  );
}

function WatchLinks({ watch, title }) {
  if (!watch?.flatrate?.length) {
    return (
      <div className="hb-watch hb-watch--empty">
        Not currently streaming on your services. {watch?.link ? (
          <a href={watch.link} target="_blank" rel="noreferrer noopener">See all options →</a>
        ) : null}
      </div>
    );
  }

  return (
    <div className="hb-watch">
      <div className="hb-watch__label">Watch on:</div>
      <div className="hb-watch__buttons">
        {watch.flatrate.map((p) => {
          const svc = services.services.find((s) => s.tmdbProviderId === p.provider_id);
          if (!svc) return null;
          const url = svc.watchUrlBase + encodeURIComponent(title);
          return (
            <a key={p.provider_id} className="hb-watch__btn" href={url}
               target="_blank" rel="noreferrer noopener"
               style={{ background: svc.color, color: pickTextColor(svc.color) }}>
              {svc.name} →
            </a>
          );
        })}
      </div>
    </div>
  );
}

function pickTextColor(hex) {
  const c = hex.replace('#', '');
  const r = parseInt(c.substr(0, 2), 16);
  const g = parseInt(c.substr(2, 2), 16);
  const b = parseInt(c.substr(4, 2), 16);
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255 > 0.6 ? '#1B3F8B' : '#FFFFFF';
}
